import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TimeIcon from "@mui/icons-material/AccessTime";
import PendingIcon from "@mui/icons-material/HourglassEmpty";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Job } from "../../../store/jobStore";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import {
  getJobElapsedTimeLabel,
  getStatusBackground,
  getStatusBorderColor,
  isJobOpenable,
} from "../helpers";
import { slideInAnimation } from "../animations";
import { JobStatusContent } from "./JobStatusContent";
import { Confetti } from "./Confetti";
import { getAwaitingInputChipSx } from "@/shared/utils/awaitingInputStyles";
import { getAwaitingInputAriaLabel } from "../../../features/hitl/presentPlan";
import { useHitlEnabled } from "@/shared/hooks/useHitlEnabled";

export interface JobCardProps {
  job: Job;
  onRemove: (job: Job, event: React.MouseEvent) => void;
  onRetry: (job: Job, event: React.MouseEvent) => void;
  onClick: (job: Job) => void;
  isNew: boolean;
  retryingJob: boolean;
  showConfetti: boolean;
}

export const JobCard: React.FC<JobCardProps> = ({
  job,
  onRemove,
  onRetry,
  onClick,
  isNew,
  retryingJob,
  showConfetti,
}) => {
  const theme = useTheme();
  const hitlEnabled = useHitlEnabled();
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchDelta, setTouchDelta] = useState(0);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const delta = touchStart - e.touches[0].clientX;
    if (delta > 0) setTouchDelta(Math.min(delta, 100));
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchDelta > 80) {
      setIsRemoving(true);
      setTimeout(() => onRemove(job, e as unknown as React.MouseEvent), 200);
    }
    setTouchStart(null);
    setTouchDelta(0);
  };

  const statusChipProps = useMemo(() => {
    switch (job.status) {
      case ReportJobStatus.PENDING_REVIEW:
        return {
          label: "Ready",
          color: "default" as const,
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
          chipSx: { bgcolor: "#FB8C00", color: "#fff", "& .MuiChip-icon": { color: "#fff" } },
        };
      case ReportJobStatus.AWAITING_INPUT:
        return {
          label: "Awaiting Input",
          color: "default" as const,
          icon: <PendingActionsIcon sx={{ fontSize: 16 }} />,
          chipSx: getAwaitingInputChipSx(),
        };
      case ReportJobStatus.AWAITING_TRANSCRIPT_DECISION:
        return {
          label: "Action needed",
          color: "default" as const,
          icon: <PendingActionsIcon sx={{ fontSize: 16 }} />,
          chipSx: getAwaitingInputChipSx(),
        };
      case ReportJobStatus.FAILED:
        return { label: "Failed", color: "error" as const, icon: <ErrorIcon sx={{ fontSize: 16 }} />, chipSx: {} };
      default:
        return { label: "Processing", color: "primary" as const, icon: <PendingIcon sx={{ fontSize: 16 }} />, chipSx: {} };
    }
  }, [job.status]);

  return (
    <Collapse in={!isRemoving} timeout={200}>
      <Paper
        elevation={2}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          mb: 2,
          position: "relative",
          overflow: "hidden",
          cursor: isJobOpenable(job.status) ? "pointer" : "default",
          backgroundColor: getStatusBackground(job.status, theme),
          borderLeft: `4px solid ${getStatusBorderColor(job.status, theme)}`,
          borderRadius: "12px",
          transform: `translateX(-${touchDelta}px)` ,
          transition: touchDelta === 0 ? "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" : "none",
          animation: isNew ? `${slideInAnimation} 0.4s ease-out` : "none",
          "&:hover": {
            boxShadow: isJobOpenable(job.status) ? 6 : 3,
            transform: isJobOpenable(job.status) ? "translateY(-2px)" : "none",
          },
        }}
        onClick={() => onClick(job)}
        role={isJobOpenable(job.status) ? "button" : undefined}
        aria-label={
          job.status === ReportJobStatus.PENDING_REVIEW
            ? "View completed report"
            : job.status === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION
              ? "Review videos that couldn't be transcribed"
              : job.status === ReportJobStatus.AWAITING_INPUT
                ? getAwaitingInputAriaLabel(hitlEnabled)
                : undefined
        }
      >
        <Confetti show={showConfetti} />

        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: touchDelta,
            backgroundColor: "error.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: touchDelta / 100,
          }}
        >
          <CloseIcon sx={{ color: "white" }} />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            pt: 1,
            px: 1,
            pb: 1,
          }}
        >
          <Box sx={{ flex: 1, pr: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.3, mb: 0.5, color: "text.primary" }}>
              {job.reportName || `Report from ${job.evidenceIDs.length} ${job.evidenceIDs.length === 1 ? "video" : "videos"}`}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <TimeIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              <Typography variant="caption" sx={{ fontSize: "0.8rem", color: "text.disabled" }}>
                {getJobElapsedTimeLabel(job)}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              label={statusChipProps.label}
              color={statusChipProps.color}
              size="small"
              icon={statusChipProps.icon}
              sx={{ fontWeight: "medium", borderRadius: "16px", ...statusChipProps.chipSx }}
            />
            <Tooltip title="Remove from list">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsRemoving(true);
                  setTimeout(() => onRemove(job, e), 200);
                }}
                sx={{
                  opacity: 0.5,
                  "&:hover": {
                    opacity: 1,
                    backgroundColor: "error.light",
                    color: "white",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {job.videoTitles && job.videoTitles.length > 0 && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography
              variant="overline"
              sx={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "text.disabled", letterSpacing: "0.08em", mb: 0.5 }}
            >
              Input Videos
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {job.videoTitles.map((title: string, idx: number) => (
                <Typography
                  component="li"
                  key={idx}
                  sx={{
                    fontSize: "0.875rem",
                    color: "text.secondary",
                    mb: 0.25,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    lineHeight: 1.4,
                  }}
                >
                  {title}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ px: 2, pb: 2 }}>
          <JobStatusContent
            job={job}
            onRetry={onRetry}
            onOpen={(event) => {
              event.stopPropagation();
              onClick(job);
            }}
            retryingJob={retryingJob}
          />
        </Box>
      </Paper>
    </Collapse>
  );
};
